import { useEffect, useRef } from "react";
import type { Alert, Lead } from "./api-client";

type DashboardEvent =
  | { type: "message"; payload: { leadId: number; message: any } }
  | { type: "status_update"; payload: { leadId: number } }
  | { type: "lead_updated"; payload: { lead: Lead } }
  | { type: "alert_created"; payload: { alert: Alert; lead: Lead | null } }
  | { type: "alert_acked"; payload: { alertId: number; leadId: number } }
  | { type: "connected"; payload: unknown }
  | { type: "error"; payload: unknown };

const wsUrl = () => {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  // Pages Functions route for directory `functions/api/ws-dashboard/index.ts` requires trailing slash.
  return `${proto}//${window.location.host}/api/ws-dashboard/`;
};

let wsSingleton: WebSocket | null = null;
let reconnectTimer: number | null = null;
let backoffMs = 250;
let visibilityBound = false;
const listeners = new Set<(evt: DashboardEvent) => void>();

const ensureConnected = () => {
  if (wsSingleton) return;
  if (listeners.size === 0) return;

  const ws = new WebSocket(wsUrl());
  wsSingleton = ws;

  ws.onopen = () => {
    backoffMs = 250;
  };

  ws.onmessage = (evt) => {
    try {
      const parsed = JSON.parse(String(evt.data)) as DashboardEvent;
      for (const l of listeners) l(parsed);
    } catch {
      // ignore malformed payloads
    }
  };

  ws.onclose = () => {
    wsSingleton = null;
    if (listeners.size === 0) return;
    if (document.visibilityState === "hidden") return;
    const delay = Math.min(backoffMs, 15000);
    backoffMs = Math.min(backoffMs * 2, 15000);
    reconnectTimer = window.setTimeout(ensureConnected, delay);
  };

  if (!visibilityBound) {
    visibilityBound = true;
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") ensureConnected();
    });
  }
};

const teardownIfIdle = () => {
  if (listeners.size > 0) return;
  if (reconnectTimer) window.clearTimeout(reconnectTimer);
  reconnectTimer = null;
  wsSingleton?.close();
  wsSingleton = null;
  backoffMs = 250;
};

export const useDashboardRealtime = (input: {
  onLeadMessage?: (payload: { leadId: number; createdAt: number; role?: "user" | "assistant"; content?: string }) => void;
  onLeadUpdated?: (lead: Lead) => void;
  onAlertCreated?: (payload: { alert: Alert; lead: Lead | null }) => void;
  onAlertAcked?: (payload: { alertId: number; leadId: number }) => void;
}) => {
  const inputRef = useRef(input);
  inputRef.current = input;

  useEffect(() => {
    const listener = (evt: DashboardEvent) => {
      if (evt.type === "message") {
        const msg = evt.payload.message as any;
        const createdAt = typeof msg?.createdAt === "number" ? msg.createdAt : Date.now();
        const role = msg?.role === "user" || msg?.role === "assistant" ? msg.role : undefined;
        const content = typeof msg?.content === "string" ? msg.content : undefined;
        inputRef.current.onLeadMessage?.({ leadId: evt.payload.leadId, createdAt, role, content });
      }
      if (evt.type === "lead_updated") {
        inputRef.current.onLeadUpdated?.(evt.payload.lead);
      }
      if (evt.type === "alert_created") {
        inputRef.current.onAlertCreated?.(evt.payload);
      }
      if (evt.type === "alert_acked") {
        inputRef.current.onAlertAcked?.(evt.payload);
      }
    };

    listeners.add(listener);
    ensureConnected();

    return () => {
      listeners.delete(listener);
      teardownIfIdle();
    };
  }, []);
};
